/**
 * 管理员认证相关 API
 */
import { request } from '@umijs/max';

type WrappedResponse<T> = {
  code?: number;
  message?: string;
  data?: T;
};

function unwrapData<T>(payload: T | WrappedResponse<T>): T {
  if (
    payload
    && typeof payload === 'object'
    && 'code' in (payload as Record<string, unknown>)
    && 'data' in (payload as Record<string, unknown>)
  ) {
    const wrapped = payload as WrappedResponse<T>;
    if (wrapped.data !== undefined) {
      return wrapped.data;
    }
  }
  return payload as T;
}

function normalizeLoginResult(payload: API.AdminLoginResult | WrappedResponse<API.AdminLoginResult>) {
  const data = unwrapData(payload);
  const token = (data as any)?.token || (data as any)?.access_token;
  const admin = (data as any)?.admin || (data as any)?.admin_user;
  if (!token || !admin) {
    throw new Error('登录响应缺少 token 或管理员信息');
  }
  return {
    ...(data as any),
    token,
    admin,
  } as API.AdminLoginResult;
}

/** 管理员登录 */
export async function adminLogin(params: {
  username: string;
  password: string;
}) {
  const result = await request<API.AdminLoginResult | WrappedResponse<API.AdminLoginResult>>('/api/v1/admin/auth/login', {
    method: 'POST',
    data: params,
    skipErrorHandler: true,
  });
  return normalizeLoginResult(result);
}

/** 获取当前管理员信息 */
export async function getAdminInfo() {
  return request<API.AdminUser>('/api/v1/admin/auth/me', {
    method: 'GET',
    skipErrorHandler: true,
  });
}

/** 修改密码 */
export async function changePassword(params: {
  old_password: string;
  new_password: string;
}) {
  return request('/api/v1/admin/auth/change-password', {
    method: 'POST',
    data: params,
  });
}

/** 创建管理员 */
export async function createAdmin(params: {
  username: string;
  password: string;
  display_name?: string;
}) {
  return request<API.AdminUser>('/api/v1/admin/auth/create', {
    method: 'POST',
    data: params,
  });
}

/** 获取管理员列表 */
export async function getAdminList(params: {
  page: number;
  page_size: number;
}) {
  return request<API.PaginatedResponse<API.AdminUser>>('/api/v1/admin/auth/admins', {
    method: 'GET',
    params,
  });
}
